import React, { useState, useEffect } from 'react';
import { HiSearch, HiTrash } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { API } from '../config';
import axios from 'axios';
import './DeletedData.css';

const DeletedWorkOrders = () => {
  const { user } = useAuth();
  const { effectiveBranchId } = useBranch();
  const { error } = useNotification();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user?.business_id || '');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API}/businesses/all`)
        .then(res => {
          const list = res.data.businesses || [];
          setBusinesses(list);
          if (!selectedBusinessId && list.length > 0) setSelectedBusinessId(list[0].id);
        })
        .catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    const businessId = user?.role === 'super_admin' ? selectedBusinessId : user?.business_id;
    if (!businessId) {
      setLoading(false);
      setWorkOrders([]);
      return;
    }
    setLoading(true);
    const params = effectiveBranchId ? `?branchId=${effectiveBranchId}` : '';
    axios.get(`${API}/work-orders/business/${businessId}/deleted${params}`)
      .then(res => setWorkOrders(res.data.work_orders || []))
      .catch(err => error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [selectedBusinessId, user?.role, user?.business_id, effectiveBranchId]);

  const filtered = workOrders.filter(wo =>
    !searchTerm ||
    (wo.work_order_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (wo.batch_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (wo.vendor?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (wo.tailor?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="deleted-data-page">
        <div className="page-header">
          <div>
            <h1><HiTrash className="page-icon" /> Deleted Work Orders</h1>
            <p className="page-subtitle">View soft-deleted work orders</p>
          </div>
        </div>
        <div className="filters-container">
          {user?.role === 'super_admin' && (
            <div className="filter-dropdown">
              <select value={selectedBusinessId} onChange={(e) => setSelectedBusinessId(e.target.value)}>
                <option value="">Select Business</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="search-bar">
            <HiSearch className="search-icon" />
            <input
              placeholder="Search by work order #, batch, vendor, or tailor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {loading ? (
          <Loader message="Loading deleted work orders..." variant="block" />
        ) : (
          <div className="table-container">
            {filtered.length === 0 ? (
              <div className="empty-state">
                <p>No deleted work orders found.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Work Order #</th>
                    <th>Batch</th>
                    <th>Branch</th>
                    <th>Vendor</th>
                    <th>Tailor</th>
                    <th>Input Product</th>
                    <th>Output Product</th>
                    <th>Status</th>
                    <th>Deleted At</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(wo => (
                    <tr key={wo.id}>
                      <td className="font-mono">{wo.work_order_number || '—'}</td>
                      <td>{wo.batch_number || '—'}</td>
                      <td>{wo.branch?.name || '—'}</td>
                      <td>{wo.vendor?.name || '—'}</td>
                      <td>{wo.tailor?.name || '—'}</td>
                      <td>{wo.input_product?.name || '—'}</td>
                      <td>{wo.output_product?.name || '—'}</td>
                      <td><span className={`status-badge ${wo.status}`}>{wo.status || '—'}</span></td>
                      <td>{formatDate(wo.deleted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DeletedWorkOrders;
